import React, { useRef, useState } from 'react';
import { Edit3, Eye, MoreVertical, Trash2 } from 'lucide-react';
import { TableActionMenu } from '../TableActionMenu/TableActionMenu';
import { useClickOutside } from '../../../hooks/useClickOutside';
import './RowActionsMenu.css';

export interface RowAction {
  label: string;
  icon: React.ElementType;
  onSelect: () => void;
  /** Color al pasar el ratón: verde (accent) o rojo para acciones destructivas (danger). */
  tone?: 'accent' | 'danger';
  disabled?: boolean;
  /** Tooltip. Con `disabled`, explica por qué no se puede. */
  title?: string;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  /** Avisa al abrir y cerrar, para que la vista cierre sus otros desplegables. */
  onOpenChange?: (open: boolean) => void;
}

// Acciones estándar: misma etiqueta e icono en todas las tablas.
export const viewAction = (onSelect: () => void): RowAction => ({
  label: 'Ver',
  icon: Eye,
  tone: 'accent',
  onSelect
});

export const editAction = (onSelect: () => void): RowAction => ({
  label: 'Editar',
  icon: Edit3,
  tone: 'accent',
  onSelect
});

export const deleteAction = (onSelect: () => void, extra?: Partial<RowAction>): RowAction => ({
  label: 'Eliminar',
  icon: Trash2,
  tone: 'danger',
  onSelect,
  ...extra
});

/**
 * Botón ⋮ de la columna de acciones y su menú. Gestiona él mismo si está
 * abierto: se cierra al elegir una acción o al hacer clic fuera, y como abrir
 * otro menú es un clic fuera, nunca hay dos abiertos a la vez.
 */
export const RowActionsMenu = ({ actions, onOpenChange }: RowActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  const setOpen = (open: boolean) => {
    setIsOpen(open);
    if (onOpenChange) onOpenChange(open);
  };

  // El menú va en un portal fuera de la fila, así que hay que reconocerlo
  // aparte del botón: si no, pulsar una acción lo cerraría antes de ejecutarla.
  useClickOutside(
    (target) => Boolean(
      (triggerRef.current && triggerRef.current.contains(target)) ||
      (itemsRef.current && itemsRef.current.contains(target))
    ),
    () => setOpen(false),
    isOpen
  );

  return (
    <div className="row-actions-menu">
      <button
        ref={triggerRef}
        type="button"
        data-keep-detail
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!isOpen);
        }}
        className={`row-actions-menu-trigger ${isOpen ? 'active' : ''}`}
      >
        <MoreVertical size={16} />
      </button>

      <TableActionMenu isOpen={isOpen} anchorEl={triggerRef.current} className="row-actions-menu-popover">
        <div ref={itemsRef} className="row-actions-menu-items">
          {actions.map(({ label, icon: Icon, onSelect, tone, disabled, title }) => (
            <button
              key={label}
              type="button"
              disabled={disabled}
              title={title}
              onClick={() => {
                setOpen(false);
                onSelect();
              }}
              className={`row-actions-menu-item ${tone || ''}`}
            >
              <Icon size={12} className="row-actions-menu-item-icon" />
              {label}
            </button>
          ))}
        </div>
      </TableActionMenu>
    </div>
  );
};
